import axios from 'axios';

const API_BASE_URL = 'https://koaapi.zinvos.com';

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          // Try to refresh the token
          const response = await axios.post(`${API_BASE_URL}/api/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login/', { username, password });
    
    // Store tokens if present
    if (response.data.tokens) {
      localStorage.setItem('access_token', response.data.tokens.access);
      localStorage.setItem('refresh_token', response.data.tokens.refresh);
    }
    
    return response.data;
  },
  
  logout: async () => {
    try {
      const response = await api.post('/api/auth/logout/');
      return response.data;
    } finally {
      // Clear tokens regardless of API response
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },
  
  checkAuth: async () => {
    const response = await api.get('/api/auth/check/');
    return response.data;
  },
};

export const membersAPI = {
  getAll: async (page = 1, search = '') => {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (search) params.append('search', search);
    const response = await api.get(`/api/members/?${params.toString()}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/members/${id}/`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/api/members/', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/members/${id}/`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/members/${id}/`);
    return response.data;
  },

  importCSV: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/api/members/import-csv/', formData, {
      headers: {
        // Let axios/browser set the correct multipart boundary.
        'Content-Type': undefined,
      },
    });

    return response.data;
  },
};

export default api;

